<template>
	<dialog-component
		v-if="visible"
		custom-class="payment-wrap"
		:close-on-backdrop="canDismiss"
		:show-close="isFailed"
		@close="onClose"
	>
		<p class="payment-head">
			<payment-status-icon :state="iconState" />
			<span class="payment-title">{{ title }}</span>
		</p>
		<p class="payment-description">{{ description }}</p>

		<div class="payment-btn-group">
			<base-button :text="ctaText" @click="onCta" data-test="payment-cta" />
		</div>
	</dialog-component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import PaymentStatusIcon from '@/components/billing/PaymentStatusIcon.vue'
import { PAYMENT_DIALOG_TEXTS, PAYMENT_FAILURE_FALLBACK, PAYMENT_FAILURE_TEXTS } from '@/constants/pro'
import { useBillingStore } from '@/stores/billing'
import { usePlansStore } from '@/stores/plans'
import { usePaymentFlow } from '@/composables/usePaymentFlow'

const billing = useBillingStore()
const plansStore = usePlansStore()
const { startCheckout } = usePaymentFlow()

const isConfirming = computed(() => billing.paymentState === 'confirming')
const isSucceeded = computed(() => billing.paymentState === 'succeeded')
const isFailed = computed(() => billing.paymentState === 'failed')

const visible = computed(() => billing.dialogVisible && (isConfirming.value || isSucceeded.value || isFailed.value))

const canDismiss = computed(() => !isConfirming.value)

const iconState = computed(() => {
	if (isSucceeded.value) return 'succeeded'
	if (isFailed.value) return 'failed'
	return 'confirming'
})

const title = computed(() => {
	if (isSucceeded.value) return PAYMENT_DIALOG_TEXTS.succeeded.title
	if (isFailed.value) return PAYMENT_DIALOG_TEXTS.failed.title
	return PAYMENT_DIALOG_TEXTS.confirming.title
})

const description = computed(() => {
	if (isSucceeded.value) return plansStore.successDescription
	if (isFailed.value) {
		const reason = billing.failureReason
		return (reason && PAYMENT_FAILURE_TEXTS[reason]) || PAYMENT_FAILURE_FALLBACK
	}
	return PAYMENT_DIALOG_TEXTS.confirming.description
})

const ctaText = computed(() => {
	if (isSucceeded.value) return PAYMENT_DIALOG_TEXTS.succeeded.cta
	if (isFailed.value) return PAYMENT_DIALOG_TEXTS.failed.cta
	return PAYMENT_DIALOG_TEXTS.confirming.cta
})

function onCta() {
	if (isFailed.value) {
		billing.reset()
		startCheckout('retry')
		return
	}
	onClose()
}

function onClose() {
	if (isConfirming.value) {
		billing.closeDialog()
		return
	}
	billing.reset()
}
</script>

<style lang="scss" scoped>
.payment {
	&-head {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		margin-bottom: 12px;
	}
	&-title {
		font: var(--font-title);
		color: var(--text-primary);
		text-align: center;
	}
	&-description {
		font: var(--font-body-small);
		color: var(--text-secondary);
		text-align: center;
		margin-bottom: 24px;
	}
	&-btn-group {
		width: 100%;
		.btn {
			width: 100%;
			padding: 14px 12px;
			border-radius: var(--radius-md);
			min-height: 52px;
		}
	}
}
</style>
